import React, { lazy, Suspense } from 'react'
import ErrorBoundary from '@fs/zion-error-boundary'
import { Switch, Route, NotFound } from '@fs/zion-router'
import { pidRegexStr } from '@fs/zion-pid'
import { DataContext } from './services/data-service'
import { DefaultScriptsContext } from './services/default-scripts-service'
import { SurnameIndexContext } from './services/surname-index-service'
import { getExternalBaseUrl } from './services/url-service'
import { appSupportedLocales } from './types/appSupportedLocales'
import LoadingPage from './components/AncestorPage/LoadingPage'

const DevelopmentPage = lazy(() => import('./DevelopmentPage'))
const AncestorPage = lazy(() => import('./components/AncestorPage/AncestorPage'))
const HomePage = lazy(() => import('./components/HomePage/HomePage'))
const SearchPage = lazy(() => import('./components/search/SearchPage'))
const IndexPage = lazy(() => import('./components/surnames/IndexPage'))
const ScriptsPage = lazy(() => import('./components/surnames/ScriptsPage'))
const SavedHtmlError = lazy(() => import('./components/errors/SavedHtmlError'))
const SCRIPT_MATCH = ':script([a-z]{4})'
const PID_MATCH = `:pid(${pidRegexStr})`
const SURNAMES_MATCH = ':surnames(surnames)'
const LOCALE_MATCH = `:locale(${appSupportedLocales.join('|')})`
const CACHE_BUST_MATCH = ':cb(cb[0-9]{13})'

function DomainRedirect(): null {
  window.location.href = `${getExternalBaseUrl()}${window.location.pathname}`
  return null
}
// The /tree/ancestors path variants are only needed for localhost dev with the frontier_dtmulator flag on, which
// still serves the app at /tree/ancestors (in production the appPathOverride in server.js makes them unnecessary).
// They can be removed once snow's dtmulator excepts this app; see fs-webdev/snow commits da75175 (exception added
// for tree-ancestors-r9) and 90bfbfe (removed), and https://familysearch.slack.com/archives/C0FPL3ZL7/p1734976403633959

const DEFAULT_PATHS = ['/', `/${CACHE_BUST_MATCH}`, '/tree/ancestors/', `/tree/ancestors/${CACHE_BUST_MATCH}`]

const SEARCH_PATHS = [
  '/search',
  `/search/${CACHE_BUST_MATCH}`,
  '/tree/ancestors/search',
  `/tree/ancestors/search/${CACHE_BUST_MATCH}`,
]

const SURNAMES_DEFAULT_PATHS = [
  `/${SURNAMES_MATCH}`,
  `/${SURNAMES_MATCH}/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}/${CACHE_BUST_MATCH}`,
]

const SURNAMES_SCRIPT_PATHS = [
  `/${SURNAMES_MATCH}/${SCRIPT_MATCH}`,
  `/${SURNAMES_MATCH}/${SCRIPT_MATCH}/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}/${SCRIPT_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}/${SCRIPT_MATCH}/${CACHE_BUST_MATCH}`,
]

const SURNAMES_SCRIPT_ETC_PATHS = [
  `/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*`,
  `/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*`,
  `/tree/ancestors/${SURNAMES_MATCH}/${SCRIPT_MATCH}/*/${CACHE_BUST_MATCH}`,
]

const PID_PATHS = [
  `/${PID_MATCH}`,
  `/${PID_MATCH}/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${PID_MATCH}`,
  `/tree/ancestors/${PID_MATCH}/${CACHE_BUST_MATCH}`,
]

export const OLD_ANCESTOR_PAGE_PATHS = [
  `/${PID_MATCH}/:nameAndLifespan`,
  `/${PID_MATCH}/:nameAndLifespan/${CACHE_BUST_MATCH}`,
  `/${LOCALE_MATCH}/${PID_MATCH}/:nameAndLifespan`,
  `/${LOCALE_MATCH}/${PID_MATCH}/:nameAndLifespan/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${PID_MATCH}/:nameAndLifespan`,
  `/tree/ancestors/${PID_MATCH}/:nameAndLifespan/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${LOCALE_MATCH}/${PID_MATCH}/:nameAndLifespan`,
  `/tree/ancestors/${LOCALE_MATCH}/${PID_MATCH}/:nameAndLifespan/${CACHE_BUST_MATCH}`,
]

export const ANCESTOR_PAGE_PATHS = [
  `/${PID_MATCH}/:nameAndLifespan`,
  `/${PID_MATCH}/:nameAndLifespan/${CACHE_BUST_MATCH}`,
  `/tree/ancestors/${PID_MATCH}/:nameAndLifespan`,
  `/tree/ancestors/${PID_MATCH}/:nameAndLifespan/${CACHE_BUST_MATCH}`,
]

export default function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingPage />}>
        <Switch>
          {process.env.NODE_ENV !== 'production' && <Route exact path="/dev" component={DevelopmentPage} />}

          <Route exact path={DEFAULT_PATHS}>
            <DefaultScriptsContext>
              <HomePage />
            </DefaultScriptsContext>
          </Route>

          <Route exact path={SEARCH_PATHS}>
            <SearchPage />
          </Route>

          <Route exact path={SURNAMES_DEFAULT_PATHS}>
            <SurnameIndexContext>
              <ScriptsPage />
            </SurnameIndexContext>
          </Route>

          <Route exact path={SURNAMES_SCRIPT_PATHS}>
            <SurnameIndexContext>
              <IndexPage />
            </SurnameIndexContext>
          </Route>

          <Route exact path={SURNAMES_SCRIPT_ETC_PATHS}>
            <SurnameIndexContext>
              <IndexPage />
            </SurnameIndexContext>
          </Route>

          <Route exact path={PID_PATHS}>
            <DataContext>
              <AncestorPage />
            </DataContext>
          </Route>

          <Route exact path={ANCESTOR_PAGE_PATHS}>
            <DataContext>
              <AncestorPage />
            </DataContext>
          </Route>
          <Route
            path={[
              '*/Desktop/*',
              '*/Users/*',
              '*/USUARIOS/*',
              '*/OneDrive/*',
              '*/Museum/*',
              '*/Personal/*',
              '*/Pictures/*',
            ]}
          >
            <DataContext>
              <SavedHtmlError />
            </DataContext>
          </Route>

          <Route path={['/tree/person/*', '/tree/pedigree/*']}>
            <DomainRedirect />
          </Route>

          <Route path="*" component={NotFound} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  )
}
